<template>
  <Dialog :open="isOpen" @update:open="$emit('update:isOpen', $event)">
    <DialogScrollContent class="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>Customization Demos</DialogTitle>
        <DialogDescription>
          Show Toolkit as it appears for a district running each customization. Your own profile is
          not changed, and every demo stays on until you turn it off or close this tab.
        </DialogDescription>
      </DialogHeader>

      <p v-if="error" class="text-sm text-red-700" role="alert">
        Could not check which customizations are available. Close this and try again.
      </p>

      <div class="divide-y">
        <section v-for="customization in catalog" :key="customization.key" class="py-4">
          <div class="flex items-start gap-4">
            <div class="flex-1 min-w-0" :class="{ 'opacity-60': !isOnThisPage(customization) }">
              <h3 class="text-md font-bold">{{ customization.title }}</h3>
              <p class="text-sm text-muted-foreground">{{ customization.description }}</p>
            </div>
            <select
              class="w-56 shrink-0 rounded border border-gray-300 p-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
              :aria-label="`Demo ${customization.title} as`"
              :disabled="fetching || !isOnThisPage(customization)"
              :value="selectedProfileFor(customization.key)"
              @change="selectProfile(customization.key, ($event.target as HTMLSelectElement).value)">
              <option :value="NONE">None</option>
              <option
                v-for="category in customization.categories"
                :key="category.profileId"
                :value="category.profileId"
                :disabled="!isProfileAvailable(customization.key, category.profileId)">{{ categoryLabel(customization.key, category) }}</option>
            </select>
          </div>

          <p v-if="!isOnThisPage(customization)" class="mt-2 text-sm text-muted-foreground">
            Not available on this page.
          </p>

          <a
            v-for="link in customization.demoLinks"
            :key="link.url"
            :href="link.url"
            class="mt-2 block text-sm text-blue-600 hover:underline">
            {{ link.label }} &rarr;
          </a>
        </section>
      </div>

      <DialogFooter>
        <GbButton variant="secondary" :disabled="!isDemoActive" @click="demoStore.turnOffAll()">
          Turn off all demos
        </GbButton>
        <GbButton @click="$emit('update:isOpen', false)">Close</GbButton>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  Dialog,
  DialogScrollContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/dialog'
import GbButton from '@/components/GbButton.vue'
import { useCustomizationDemoStore } from './customization-demo.store'
import {
  CUSTOMIZATION_CATALOG,
  isCustomizationOnPage,
  type CustomizationCatalogEntry,
  type CustomizationKey,
  type DemoCategory
} from './customization_demo_catalog'

const NONE = ''

const props = defineProps<{
  isOpen: boolean
}>()

defineEmits<{
  'update:isOpen': [value: boolean]
}>()

const catalog = CUSTOMIZATION_CATALOG
const demoStore = useCustomizationDemoStore()
const route = useRoute()
const isDemoActive = computed(() => demoStore.isDemoActive)
const fetching = computed(() => demoStore.fetching)
const error = computed(() => demoStore.error)

// Checked on every open rather than once: a rubric released in Airtable mid-session should show up
// without a reload, and a selection that has gone stale gets dropped here.
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) demoStore.fetchAvailability()
  },
  { immediate: true }
)

const isOnThisPage = (customization: CustomizationCatalogEntry) =>
  isCustomizationOnPage(customization, route.path)

const selectedProfileFor = (customization: CustomizationKey) =>
  demoStore.demoProfileFor(customization) || NONE

const isProfileAvailable = (customization: CustomizationKey, profileId: string) =>
  demoStore.isProfileAvailable(customization, profileId)

const categoryLabel = (customization: CustomizationKey, category: DemoCategory) =>
  isProfileAvailable(customization, category.profileId)
    ? category.label
    : `${category.label} (unavailable)`

const selectProfile = (customization: CustomizationKey, profileId: string) =>
  demoStore.selectProfile(customization, profileId)
</script>
